import React from 'react';
import { Upload, Images, FileText, Microscope, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ImportContentType {
  type: 'images' | 'research' | 'pages';
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: 'blue' | 'green' | 'purple';
  description: string;
  storageContentType: 'private_image' | 'private_research' | 'private_page';
}

interface ImportContentDropdownProps {
  onContentTypeSelect: (contentType: 'images' | 'research' | 'pages') => void;
  className?: string;
}

const importContentTypes: ImportContentType[] = [
  {
    type: 'images',
    name: 'Images',
    icon: Images,
    variant: 'blue',
    description: 'Add your images to Storage',
    storageContentType: 'private_image'
  },
  {
    type: 'research',
    name: 'Research',
    icon: Microscope,
    variant: 'green',
    description: 'Add your research to Storage',
    storageContentType: 'private_research'
  },
  {
    type: 'pages',
    name: 'Documents',
    icon: FileText,
    variant: 'purple',
    description: 'Add your documents to Storage',
    storageContentType: 'private_page'
  }
];

const getVariantStyles = (variant: 'blue' | 'green' | 'purple') => {
  switch (variant) {
    case 'blue':
      return 'text-blue-600 dark:text-blue-400';
    case 'green':
      return 'text-green-600 dark:text-green-400';
    case 'purple':
      return 'text-purple-600 dark:text-purple-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

export const ImportContentDropdown: React.FC<ImportContentDropdownProps> = ({
  onContentTypeSelect,
  className
}) => {
  const handleContentTypeSelect = (contentType: 'images' | 'research' | 'pages') => {
    onContentTypeSelect(contentType);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="default"
          className={cn(
            // Primary button styling - different from filter dropdowns
            "w-full justify-between h-10",
            "bg-gradient-to-r from-blue-600 to-purple-600",
            "hover:from-blue-700 hover:to-purple-700",
            "text-white font-medium",
            "border border-blue-500/20",
            "shadow-lg hover:shadow-xl",
            "transition-all duration-200",
            className
          )}
        >
          <div className="flex items-center gap-2">
            <Upload size={16} />
            <span>Add to Storage</span>
          </div>
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        className="w-[var(--radix-dropdown-menu-trigger-width)] bg-white/95 dark:bg-black/95 backdrop-blur-md border border-accent/20"
        sideOffset={4}
      >
        <div className="p-1 space-y-1">
          {importContentTypes.map((contentType) => {
            const IconComponent = contentType.icon;
            return (
              <DropdownMenuItem
                key={contentType.type}
                onClick={() => handleContentTypeSelect(contentType.type)}
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/10 rounded-md"
              >
                <IconComponent 
                  className={cn("h-5 w-5", getVariantStyles(contentType.variant))} 
                />
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{contentType.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {contentType.description}
                  </span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ImportContentDropdown; 